import React, { useState, useEffect } from 'react';
import { Card, Spin, Typography, Empty } from 'antd';
import { DualAxes } from '@ant-design/charts';
import { useTranslation } from 'react-i18next';
import { fetchKeepaHistory } from '../../shared/api/client';
import { useAppColors, useChartTheme } from '../../shared/theme';

// Map i18n language → Intl date locale tag for short month/day labels.
const DATE_LOCALES: Record<string, string> = {
  ru: 'ru-RU',
  uk: 'uk-UA',
  en: 'en-US',
};

interface BsrChartProps {
  asin: string;
}

interface ChartRow {
  date: string;
  /** Ось X — время, а не список дней: подписи ставятся по месяцам, а не как решит G2. */
  day: Date;
  ts: number;
  bsr: number | null;
  price: number | null;
}

const BsrChart: React.FC<BsrChartProps> = ({ asin }) => {
  const { t, i18n } = useTranslation();
  const c = useAppColors();
  const chartTheme = useChartTheme();
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartRow[]>([]);
  const [error, setError] = useState('');
  // Свёрнут по умолчанию (раскладка «вариант 4», фидбек 29.09): данные грузятся
  // сразу, график раскрывается по клику.
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if (!asin) return;
    let cancelled = false;
    setLoading(true);
    setError('');

    // Match Intl locale to active i18n language so axis labels are localized.
    const baseLang = (i18n.language || 'en').toLowerCase().split('-')[0];
    const dateLocale = DATE_LOCALES[baseLang] || 'en-US';

    fetchKeepaHistory(asin, 365)
      .then((res) => {
        if (cancelled) return;

        // Collect all timestamps and build maps
        const allTimestamps = new Set<number>();
        const bsrMap = new Map<number, number>();
        const priceMap = new Map<number, number>();

        for (const p of res.bsr) {
          const day = Math.floor(p.x / 86400000) * 86400000;
          allTimestamps.add(day);
          bsrMap.set(day, p.y);
        }

        for (const p of res.price) {
          const day = Math.floor(p.x / 86400000) * 86400000;
          allTimestamps.add(day);
          priceMap.set(day, p.y);
        }

        // Sort timestamps and build rows with forward-fill for price
        const sortedTs = Array.from(allTimestamps).sort((a, b) => a - b);
        let lastPrice: number | null = null;

        const rows: ChartRow[] = sortedTs.map((ts) => {
          const price = priceMap.get(ts);
          if (price !== undefined) {
            lastPrice = price;
          }
          return {
            ts,
            date: new Date(ts).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' }),
            day: new Date(ts),
            bsr: bsrMap.get(ts) ?? null,
            price: lastPrice,
          };
        });

        setChartData(rows);
      })
      .catch(() => {
        if (!cancelled) setError(t('idea_calc.bsr_chart.load_error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [asin, i18n.language, t]);

  const axisLocale = DATE_LOCALES[(i18n.language || 'en').toLowerCase().split('-')[0]] || 'en-US';

  const config = {
    // См. Summary: оси и подписи рисует G2 своей темой, не нашими токенами.
    theme: chartTheme,
    // Ось X — время: 12 делений с подписью месяца («окт 25»), чтобы график сверяли с
    // помесячным планом продаж (фидбек 29.09). По категориям-дням G2 сам прореживал
    // подписи и ставил их на случайные даты.
    xField: 'day',
    scale: { x: { type: 'time' as const, tickCount: 12 } },
    tooltip: { title: 'date' },
    data: chartData,
    height: 340,
    children: [
      {
        type: 'line' as const,
        yField: 'bsr',
        shapeField: 'smooth',
        scale: {
          y: { reverse: true },
        },
        axis: {
          y: { title: t('idea_calc.bsr_chart.axis_bsr'), titleFill: c.success },
        },
        style: { lineWidth: 1.5, stroke: c.success },
      },
      {
        type: 'line' as const,
        yField: 'price',
        shapeField: 'hv',
        scale: {
          y: { independent: true },
        },
        axis: {
          y: { position: 'right' as const, title: t('idea_calc.bsr_chart.axis_price'), titleFill: c.accent, grid: null },
        },
        style: { lineWidth: 2, stroke: c.accent },
      },
    ],
    axis: {
      x: {
        labelFormatter: (datum: Date | string | number) =>
          new Date(datum).toLocaleDateString(axisLocale, { month: 'short', year: '2-digit' }),
        labelAutoRotate: false,
      },
    },
    interaction: {
      tooltip: {
        shared: true,
        crosshairs: true,
        render: (_: unknown, { title, items }: { title: string; items: Array<{ name: string; value: number; color: string }> }) => {
          const bsrItem = items.find((i) => i.name === 'bsr');
          const priceItem = items.find((i) => i.name === 'price');
          return `
            <div style="padding: 8px 12px; font-size: 12px;">
              <div style="margin-bottom: 4px; font-weight: 500;">${title}</div>
              ${bsrItem ? `<div style="color: ${c.success};">BSR: ${bsrItem.value?.toLocaleString() ?? '—'}</div>` : ''}
              ${priceItem ? `<div style="color: ${c.accent};">Buy Box: $${priceItem.value?.toFixed(2) ?? '—'}</div>` : ''}
            </div>
          `;
        },
      },
    },
  };

  return (
    <Card
      title={`${t('idea_calc.bsr_chart.title_prefix')} — ${asin}`}
      variant="outlined"
      size="small"
      extra={
        <Typography.Link onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? t('idea_calc.bsr_chart.expand') : t('idea_calc.bsr_chart.collapse')}
        </Typography.Link>
      }
    >
      {!collapsed && (
        <Spin spinning={loading}>
          {error ? (
            <Typography.Text type="danger">{error}</Typography.Text>
          ) : chartData.length === 0 && !loading ? (
            <Empty description={t('idea_calc.bsr_chart.no_data')} />
          ) : (
            <DualAxes {...config} />
          )}
        </Spin>
      )}
    </Card>
  );
};

export default BsrChart;
